import {defineStore, skipHydrate, storeToRefs} from 'pinia';
import type {RouteLocationRaw} from 'vue-router';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';

export enum UserState {
	HP_MY_GLOBUS_BANNER_LOGIN,
	HP_MY_GLOBUS_BANNER_REGISTRATION,
	USER_MENU_ANONYMOUS_SLIDEOUT_LOGIN,
	USER_MENU_ANONYMOUS_SLIDEOUT_REGISTRATION,
	ONLINE_ORDER_BUTCHERY,
	ONLINE_ORDER_RESTAURANT,
	PRODUCT_TILE_LOGIN,
	PRODUCT_DETAIL_SHOPPING_LIST,
	PRODUCT_DETAIL_MY_GLOBUS_BANNER,
	USER_MENU_SHOPPING_LISTS,
	MY_GLOBUS_INTRODUCTORY_BANNER,
	USER_RECEIPTS_PREVIEW_LIST,
	MY_GLOBUS_REGISTRATION,
	UPDATE_PROFILE,
	UPDATE_PROFILE_PAGE_PROFILE,
	UPDATE_PROFILE_PAGE_CHANGE_EMAIL,
	UPDATE_PROFILE_PAGE_PERSONAL_INFO,
	UPDATE_PROFILE_PAGE_CONTACT_INFO,
	UPDATE_PROFILE_PAGE_PREFERENCE,
	UPDATE_PROFILE_PAGE_PREFERENCE_ALERTS,
	UPDATE_PROFILE_PAGE_INTERESTS,
	UPDATE_PROFILE_PAGE_CONSENTS,
	UPDATE_PROFILE_RECEIPTS,
	LOGIN_PAGE_PROFILE,
	LOGIN_PAGE_CHANGE_EMAIL,
	LOGIN_PAGE_PERSONAL_INFO,
	LOGIN_PAGE_CONTACT_INFO,
	LOGIN_PAGE_PREFERENCE,
	LOGIN_PAGE_INTERESTS,
	LOGIN_PAGE_CONSENTS,
	LOGIN_PAGE,
	FORGOTTEN_PASSWORD_PAGE,
	FORGOTTEN_PASSWORD_PAGE_LOGIN,
	REGISTRATION_PAGE,
	REGISTRATION_PAGE_LOGIN,
	LOGOUT,
	LOGIN_MY_ACCOUNT,
	LOGIN_COUPONS,
	LOGIN_COUPON_DETAIL,
	LOGIN_GIFT_DETAIL,
	LOGIN_ACCOUNT_SETTINGS,
	LOGIN_CASH_BONUS,
	LOGIN_RECEIPTS,
	SHOPPING_LISTS,
}

const redirects: Partial<Record<UserState, string | RouteLocationRaw>> = {
	[UserState.HP_MY_GLOBUS_BANNER_LOGIN]: '/muj-ucet',
	[UserState.HP_MY_GLOBUS_BANNER_REGISTRATION]: '/muj-ucet',
	[UserState.USER_MENU_ANONYMOUS_SLIDEOUT_REGISTRATION]: '/muj-ucet',
	[UserState.USER_MENU_SHOPPING_LISTS]: '/nakupni-seznamy',
	[UserState.MY_GLOBUS_INTRODUCTORY_BANNER]: '/muj-ucet',
	[UserState.USER_RECEIPTS_PREVIEW_LIST]: '/muj-ucet',
	[UserState.MY_GLOBUS_REGISTRATION]: '/vernostni-program',
	[UserState.LOGIN_PAGE]: '/muj-ucet',
	[UserState.FORGOTTEN_PASSWORD_PAGE]: '/muj-ucet',
	[UserState.FORGOTTEN_PASSWORD_PAGE_LOGIN]: '/muj-ucet',
	[UserState.REGISTRATION_PAGE]: '/muj-ucet',
	[UserState.REGISTRATION_PAGE_LOGIN]: '/muj-ucet',
	[UserState.LOGIN_PAGE_PROFILE]: '/profil',
	[UserState.LOGIN_PAGE_CHANGE_EMAIL]: '/muj-ucet/nastaveni-uctu/zmena-e-mailu',
	[UserState.LOGIN_PAGE_PERSONAL_INFO]: '/osobni-udaje',
	[UserState.LOGIN_PAGE_CONTACT_INFO]: '/kontaktni-udaje',
	[UserState.LOGIN_PAGE_PREFERENCE]: '/preference',
	[UserState.LOGIN_PAGE_INTERESTS]: '/zajmy',
	[UserState.LOGIN_PAGE_CONSENTS]: '/souhlasy',
	[UserState.LOGIN_MY_ACCOUNT]: '/muj-ucet',
	[UserState.LOGIN_COUPONS]: '/muj-ucet/kupony',
	[UserState.LOGIN_COUPON_DETAIL]: '/muj-ucet/kupony',
	[UserState.LOGIN_GIFT_DETAIL]: '/muj-ucet/kupony',
	[UserState.LOGIN_ACCOUNT_SETTINGS]: '/muj-ucet/nastaveni-uctu',
	[UserState.LOGIN_CASH_BONUS]: '/muj-ucet/penezni-bonus',
	[UserState.LOGIN_RECEIPTS]: '/muj-ucet/prehled-uctenek',
	[UserState.SHOPPING_LISTS]: '/nakupni-seznamy',
};

export type CrmStateRedirectOptions = {
	path?: string;
	hash?: string;
};

export const useCrmStateStore = defineStore('crmStateStore', () => {
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);
	const route = useRoute();
	const branchSpecificPath = useBranchSpecificPath();

	const state = useCookie<{state: UserState; hm: string; path: string; hash?: string} | null>(
		'crmState',
		{default: () => null},
	);

	function setState(newState: UserState, options: CrmStateRedirectOptions = {}) {
		state.value = {
			state: newState,
			hm: selectedHypermarket.value ?? '',
			path: options.path ?? route.fullPath,
			hash: options.hash ?? route.hash,
		};
	}

	function getRedirect(): string | RouteLocationRaw | null {
		if (!state.value) { return null; }

		const redirect = redirects[state.value.state];
		if (redirect) {
			return state.value.hash ? `${redirect}${state.value.hash}` : redirect;
		}

		switch (state.value.state) {
			case UserState.ONLINE_ORDER_BUTCHERY:
				return branchSpecificPath({name: 'online-objednavka-slug-shrnuti', params: {slug: 'reznictvi'}}).value;
			case UserState.ONLINE_ORDER_RESTAURANT:
				return branchSpecificPath({name: 'online-objednavka-slug-shrnuti', params: {slug: 'restaurace'}}).value;
			case UserState.PRODUCT_TILE_LOGIN:
			case UserState.PRODUCT_DETAIL_SHOPPING_LIST:
			case UserState.PRODUCT_DETAIL_MY_GLOBUS_BANNER:
			case UserState.USER_MENU_ANONYMOUS_SLIDEOUT_LOGIN:
				return state.value.path;
			default:
				return '/muj-ucet';
		}
	}

	function isUpdateProfileState() {
		return state.value?.state === UserState.UPDATE_PROFILE;
	}

	function clearState() {
		state.value = null;
	}

	return {
		state: skipHydrate(state),
		setState,
		getRedirect,
		isUpdateProfileState,
		clearState,
	};
});
